import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Form, Row, Col, Button, Container, Alert } from 'react-bootstrap'
import { addItem } from '../actions/itemActions'
import { addName } from '../actions/nameActions'
import {
  addSettings,
  updateSettings,
  getSettings
} from '../actions/settingActions'
import Loader from '../components/Loader'
import Message from '../components/Message'
import {
  EMPLOYEE_PERMISSIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

const Settings = () => {
  const [item, setItem] = useState('')
  const [manufacturer, setName] = useState('')
  const [taxrate, setTaxrate] = useState('')
  const [currency, setCurrency] = useState('')
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  })

  function showAlert(message, variant = 'success', seconds = 1000) {
    setAlert({
      show: true,
      message,
      variant
    })

    setTimeout(() => {
      setAlert({
        show: false,
        message: '',
        variant: 'success'
      })
    }, seconds)
  }

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const activeSession = ownerInfo || userInfo
  const isOwner = Boolean(ownerInfo?.isOwner)
  const canManageCatalog = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.CATALOG_MANAGE
  )
  const canAccessSettings = isOwner || canManageCatalog

  // Problem with cid when user is not logged in
  // const { cid } = userInfo

  const itemAdd = useSelector((state) => state.itemAdd)
  const { loading, error, success } = itemAdd

  const nameAdd = useSelector((state) => state.nameAdd)
  const {
    loading: loadingName,
    error: errorName,
    success: successName
  } = nameAdd

  const settingAdd = useSelector((state) => state.settingAdd)
  const {
    loading: loadingSetting,
    error: errorSetting,
    successadd
  } = settingAdd

  const settingList = useSelector((state) => state.settingList)
  const { settings } = settingList

  const onSubmit = (e) => {
    e.preventDefault()
    if (!activeSession || !activeSession.cid) {
      return
    }

    if (item) {
      const newitem = { item: item, cid: activeSession.cid }
      dispatch(addItem(newitem))
      showAlert('Item Added', 'success')
      setItem('')
    } else if (manufacturer) {
      dispatch(addName({ manufacturer, cid: activeSession.cid }))
      showAlert('Manufacturer Added', 'success')
      setName('')
    } else if (isOwner && taxrate && currency) {
      dispatch(addSettings({ taxrate, currency, cid: activeSession.cid }))
      showAlert('Tax Rate and Currency Added', 'success')
      setTaxrate('')
      setCurrency('')
    }
  }

  const onSumbitUpdate = (e) => {
    e.preventDefault()

    if (isOwner && taxrate && currency && settings) {
      dispatch(updateSettings({ taxrate, currency, _id: settings._id }))
      showAlert('Tax Rate and Currency Updated', 'success')
      setTaxrate('')
      setCurrency('')
    }
  }

  useEffect(() => {
    if (success) {
      dispatch({ type: 'ITEM_ADD_RESET' })
      navigate('/settings')
    } else if (successName && !errorName) {
      dispatch({ type: 'NAME_ADD_RESET' })
      navigate('/settings')
    }
  }, [dispatch, success, navigate, successName, errorName])

  useEffect(() => {
    if (!userInfo && !ownerInfo) {
      dispatch({ type: 'SETTING_LIST_RESET' })
      navigate('/login')
    } else if (!canAccessSettings) {
      dispatch({ type: 'SETTING_LIST_RESET' })
      navigate('/pos')
    } else if (!activeSession || activeSession.cid === undefined) {
      dispatch({ type: 'SETTING_LIST_RESET' })
      navigate('/login')
    }
  }, [
    dispatch,
    navigate,
    userInfo,
    ownerInfo,
    canAccessSettings,
    activeSession
  ])

  useEffect(() => {
    if (successadd) {
      dispatch(getSettings())
    }
  }, [dispatch, successadd])

  return (
    <Container>
      <h3 className='my-2'>Settings</h3>
      <hr />
      {alert.show && <Alert variant={alert.variant}>{alert.message}</Alert>}
      {error && <Message variant='danger'>{error}</Message>}
      {errorName && <Message variant='danger'>{errorName}</Message>}
      {errorSetting && <Message variant='danger'>{errorSetting}</Message>}
      {loading && <Loader />}
      {loadingName && <Loader />}
      {loadingSetting && <Loader />}
      {/* {loadinglist && <Loader />} */}
      <Form onSubmit={onSubmit}>
        {canManageCatalog && (
          <Row className='my-2'>
            <Col className='d-flex flex-column'>
              <Form.Group controlId='newItem'>
                <Form.Label>Add New Item</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter New Item'
                  value={item}
                  onChange={(e) => setItem(e.target.value)}
                />
              </Form.Group>
              <Button
                type='submit'
                variant='outline-primary'
                size='sm'
                className='mt-2 align-self-start settings-action-btn'
              >
                Save Item
              </Button>
            </Col>
            <Col className='d-flex flex-column'>
              <Form.Group controlId='newName'>
                <Form.Label>Add New Manufacturer</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter New Manufacturer'
                  value={manufacturer}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>
              <Button
                type='submit'
                variant='outline-primary'
                size='sm'
                className='mt-2 align-self-start settings-action-btn'
              >
                Save Manufacturer
              </Button>
            </Col>
          </Row>
        )}
        {isOwner && (
          <Row className='my-2'>
            <Col className='d-flex flex-column'>
              <Form.Group controlId='setTaxrate'>
                <Form.Label>Set Tax Rate</Form.Label>
                <Form.Control
                  type='number'
                  min='0.01'
                  step='0.01'
                  placeholder='Enter Tax Rate %'
                  className='w-50'
                  value={taxrate}
                  onChange={(e) => setTaxrate(e.target.value)}
                />
              </Form.Group>
              {settings ?
                <Button
                  type='button'
                  variant='outline-primary'
                  size='sm'
                  className='mt-2 align-self-start settings-action-btn'
                  onClick={onSumbitUpdate}
                >
                  Update Tax and Currency
                </Button>
              : <Button
                  type='submit'
                  variant='outline-primary'
                  size='sm'
                  className='mt-2 align-self-start settings-action-btn'
                >
                  Save Tax Rate and Currency
                </Button>
              }
            </Col>

            <Col>
              <Form.Group controlId='setCurrency'>
                <Form.Label>Set currency, e.g. CAD, USD, GBP ...</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter Currency'
                  className='w-50'
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
      </Form>
    </Container>
  )
}

export default Settings
